import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import {
  FaEye,
  FaPencilAlt,
  FaExclamationTriangle,
  FaTrashAlt,
} from "react-icons/fa";
import { Button } from "../../../components/ui/button";
import Modal from "../../../components/Modal";
import { DataTable } from "../../../components/DataTable";
import { pocDataMap, POCService, ServiceOnMount } from "../../../services";
import { Skeleton } from "../../../components/ui/skeleton";
import { toast } from "react-toastify";
import useUserStore from "../../../stores/userStore";
import { GoCheckCircleFill, GoLocation } from "react-icons/go";
import TableCard from "../../../components/TableCard";
import ShortenedText from "../../../components/ShortenedText.js";
import LoadingImage from "../../../components/LoadingImage.js";
import { createItemStore } from "../../../stores/createItemStore";
import StatusBadge from "../../../components/table/StatusBadge"

const columns = [
  // {
  //   accessorKey: "POCId",
  //   header: "POC ID",
  //   cell: ({ row }) => (
  //     <span>
  //       <ShortenedText text={row.getValue("POCId")} />
  //     </span>
  //   ),
  // },
  {
    accessorKey: "name",
    header: "POC Name",
    cell: ({ row }) => <span>{row.getValue("name")}</span>,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status");
      return (
        <StatusBadge status={status} />
      );
    },
  },
  {
    accessorKey: "addressLine1",
    header: "Address",
    cell: ({ row }) => <span>{row.getValue("addressLine1")}</span>,
  },
  {
    accessorKey: "postcode",
    header: "Postcode",
    cell: ({ row }) => <span>{row.getValue("postcode")}</span>,
  },
  {
    accessorKey: "city",
    header: "City",
    cell: ({ row }) => <span>{row.getValue("city")}</span>,
  },
  {
    accessorKey: "state",
    header: "State",
    cell: ({ row }) => <span>{row.getValue("state")}</span>,
  },
];

// ========================================================
// Step 1: Zustand store instantiation
// ========================================================
const storeName = "pocs";
const useItems = createItemStore(
  storeName,
  (poc) => poc.POCId,
  POCService.getPOC,
  pocDataMap
);
// ========================================================

export default function Transaction() {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { userId } = useUserStore();
  const [reload, setReload] = useState(false);
  const [selectedPocId, setSelectedPocId] = useState(null);

  // const handleConfirmDelete = async () => {
  //   setIsLoading(true); // Start loading state

  //   try {
  //     const operatorId = userId;
  //     const result = await POCService.removePoc(selectedPocId, operatorId);
  //     console.log("POC deleted successfully:", result);
  //     toast.success("🎉 POC deleted successfully!", { autoClose: 2000 });

  //     setShowDeleteModal(false);
  //   } catch (error) {
  //     console.error("Failed to delete POC:", error);
  //     toast.error("❌ Failed to delete POC", { autoClose: 2000 });
  //   } finally {
  //     setReload((prev) => !prev);
  //     setIsLoading(false);
  //   }
  // };

  const handleConfirmDelete = async () => {
    setIsLoading(true); // Start loading state

    try {
      const payload = {
        PocId: selectedPocId,
        Status: "D", // Always set to "D" for deletion
      };

      const result = await POCService.editPOCDetails(payload);
      console.log("POC deactivated (via edit):", result);
      fetchItems();

      toast.success("🎉 POC deactivated successfully!", { autoClose: 2000 });
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Failed to delete POC:", error);
      toast.error("❌ Failed to deactivate POC", { autoClose: 2000 });
    } finally {
      setReload((prev) => !prev);
      setIsLoading(false);
    }
  };

  const handleActivatePOC = async () => {
    setIsLoading(true); // Start loading state

    try {
      const payload = {
        PocId: selectedPocId,
        Status: "A", // Reactivation
      };

      const result = await POCService.editPOCDetails(payload);
      console.log("POC reactivated (via edit):", result);
      fetchItems();

      toast.success("🎉 POC activated successfully!", { autoClose: 2000 });
      setShowActivateModal(false);
    } catch (error) {
      console.error("Failed to reactivate POC:", error);
      toast.error("❌ Failed to reactivate POC", { autoClose: 2000 });
    } finally {
      setReload((prev) => !prev);
      setIsLoading(false);
    }
  };

  const actions = (row) => {
    const status = row.status;
    const baseActions = [
      {
        label: "View Details",
        icon: <FaEye />,
        onClick: () => navigate(`/poc/view/${row.POCId}`),
        variant: "dropPlain",
      },
      {
        label: "Edit Details",
        icon: <FaPencilAlt />,
        onClick: () => navigate(`/poc/edit/${row.POCId}`),
        variant: "dropPlain",
      },
      {
        label: "Activate POC",
        icon: <GoCheckCircleFill />,
        onClick: () => {
          setSelectedPocId(row.POCId);
          setShowActivateModal(true);
        },
        variant: "dropGreen",
        hidden: status === "Active",
      },
      {
        label: "Deactivate POC",
        icon: <FaTrashAlt />,
        onClick: () => {
          setSelectedPocId(row.POCId); // Store the selected POCId
          setShowDeleteModal(true); // Show delete modal
        },
        variant: "dropRed",
        hidden: status === "Deactivated",
      },
    ];
    return baseActions.filter((action) => !action.hidden);
  };

  const getPOC = ServiceOnMount(POCService.getPOC, pocDataMap, [reload]);
  console.log("getPOC:", getPOC);

  // ========================================================
  // Step 2: Zustand store and page hydration
  // ========================================================
  const {
    isLoading: isLoadingPOC,
    fetchItems,
    getSortedFiltered,
  } = useItems((state) => state);
  const data = getSortedFiltered();
  const showLoading = data.length === 0 && isLoadingPOC;
  console.log(`${storeName}:`, data);
  useEffect(() => {
    fetchItems();
  }, []);
  // ========================================================

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-4 sm:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Total Communities - With Skeleton */}
            {showLoading ? (
              // {getPOC.loading ? (
              <Skeleton className="w-full h-32 rounded-xl" />
            ) : (
              <TableCard
                heading="Total Points of Collection"
                number={data.length}
                // number={getPOC.data.length}
                trend={{ percentage: "20%", isUp: true }}
                bgColor="from-lime-50 to-lime-400"
                icon={GoLocation}
                iconColor="text-lime-400"
                borderColor="border-l-lime-400"
              />
            )}
          </div>

          <div className="bg-white shadow rounded-lg md:max-w-[calc(100vw-375px)] p-4 sm:p-6 text-sm md:mt-0">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
              <div className="text-base sm:text-lg font-semibold text-[#111827] mb-4 sm:mb-0">
                POC List
              </div>
              <div className="flex space-x-4">
                <Button onClick={() => navigate("/poc/add")}>
                  <FaPlus /> Create New
                </Button>
              </div>
            </div>
            <hr />
            {showLoading ? (
              // {getPOC.loading ? (
              <LoadingImage />
            ) : data.length > 0 ? (
              // ) : getPOC?.data.length > 0 ? (
              <DataTable
                columns={columns}
                data={data}
                // data={getPOC.data}
                actions={actions}
              />
            ) : (
              <div className="text-center text-gray-500 py-6">
                No requests found.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Activate Modal */}
      <Modal
        isOpen={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        title="Activate POC"
        description="Are you sure you want to Activate this POC?"
        icon={<GoCheckCircleFill className="text-green-500" />}
        iconColor="green"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowActivateModal(false)}
            >
              No, Cancel
            </Button>
            <Button
              // onClick={() => handleActivatePOC(data.selectedRow?.POCId)}
              onClick={() => handleActivatePOC(getPOC.selectedRow?.POCId)}
              disabled={isLoading}
            >
              {isLoading ? "Activating..." : "Yes, Activate this POC"}
            </Button>
          </div>
        }
      />

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Deactivate POC"
        description="Are you sure you want to deactivate this POC?"
        icon={<FaExclamationTriangle className="text-red-500" />}
        iconColor="red"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowDeleteModal(false)}
            >
              No, Cancel
            </Button>
            <Button
              variant={"destructive"}
              // onClick={() => handleConfirmDelete(data.selectedRow?.POCId)}
              onClick={() => handleConfirmDelete(getPOC.selectedRow?.POCId)}
              disabled={isLoading}
            >
              {isLoading ? "Deactivating..." : "Yes, Deactivate this POC"}
            </Button>
          </div>
        }
      />
    </div>
  );
}
